window.KKLQuiz = (() => {
  const PRIORITY_BOOST = 10;  // equal boost for every chosen priority (order-independent)
  const SECONDARY = 40;    // minimum % for secondary
  const FLAG = 50;         // % → flagged for recommendation
  const SEVERE = 75;       // % → consult professional note

  // Pick up to `total` questions: allocation[i] from each priority, by rank.
  function selectQuestions(priorities, q) {
    const alloc = q.selection.allocation;
    const picked = [];
    priorities.forEach((dim, i) => {
      const take = alloc[Math.min(i, alloc.length - 1)];
      const pool = q.questions
        .filter((x) => x.dim === dim)
        .sort((a, b) => a.rank - b.rank);
      picked.push.apply(picked, pool.slice(0, take));
    });
    return picked;
  }

  function compute(answers, selected, priorities, q) {
    const dimsPresent = Array.from(new Set(selected.map((x) => x.dim)));
    const raw = {}, max = {}, pct = {}, adjusted = {};

    dimsPresent.forEach((d) => {
      const items = selected.filter((x) => x.dim === d);
      raw[d] = items.reduce((s, x) => s + (Number(answers[x.id]) || 0), 0);
      max[d] = items.length * 3;
      pct[d] = max[d] ? Math.round((raw[d] / max[d]) * 100) : 0;
      adjusted[d] = pct[d];
    });

    // Equal weight for every chosen priority — click order does NOT affect scoring
    dimsPresent.forEach((d) => {
      if (priorities.indexOf(d) !== -1) {
        adjusted[d] = Math.min(100, adjusted[d] + PRIORITY_BOOST);
      }
    });

    const order = dimsPresent.slice().sort((a, b) => adjusted[b] - adjusted[a]);

    // Questions are only drawn from the visitor's chosen top-3, so the score
    // order is exactly those three — first, second, third.
    const primary = order[0];
    // Marketing-oriented: always surface the next-highest dimension as secondary
    const secondary = order.length > 1 ? order[1] : null;
    const tertiary = order.length > 2 ? order[2] : null;

    const flagged = dimsPresent.filter((d) => pct[d] >= FLAG);
    const severe = dimsPresent.filter((d) => pct[d] >= SEVERE);
    const bundle = flagged.length >= 2;

    return { raw, max, pct, adjusted, primary, secondary, tertiary, ranked: order, flagged, severe, bundle, dimsPresent };
  }

  // ---------------------------------------------------------------------
  // Customer Account persistence — backend relay architecture.
  //
  // The Shopify OAuth token endpoint does NOT allow browser-side (CORS)
  // exchange, so the quiz never touches a token:
  //   1. User clicks "Sign in to save" → hosted Shopify login (PKCE).
  //   2. Shopify redirects back to this page with ?code=…&state=…
  //   3. This page POSTs { code, code_verifier, payload } to the Vercel API
  //      (customer_account_api_url). The server exchanges the code and
  //      writes custom.health_quiz_result via the Customer Account API.
  //   4. Guests keep localStorage only — behaviour unchanged.
  // Config comes from the section: window.KKL_QUIZ_CA = { clientId, apiUrl }.
  // ---------------------------------------------------------------------

  const CA_SCOPE = 'openid email customer-account-api:full';
  const CA_VERIFIER_KEY = 'kkl_ca_code_verifier';
  const CA_STATE_KEY = 'kkl_ca_state';
  const CA_PENDING_KEY = 'kkl_ca_pending';

  const PROFILE_KEY = 'kkl_quiz_profile';

  function caClientId() {
    return (window.KKL_QUIZ_CA && window.KKL_QUIZ_CA.clientId) || '';
  }
  function caApiUrl() {
    return (window.KKL_QUIZ_CA && window.KKL_QUIZ_CA.apiUrl) || '';
  }

  function storeGet(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } }
  function storeSet(key, val) { try { sessionStorage.setItem(key, val); } catch (e) { /* noop */ } }
  function storeDel(key) { try { sessionStorage.removeItem(key); } catch (e) { /* noop */ } }

  function b64urlEncode(buf) {
    const bytes = new Uint8Array(buf);
    let str = '';
    for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function randomString(len) {
    const arr = new Uint8Array(len);
    crypto.getRandomValues(arr);
    return b64urlEncode(arr);
  }

  async function sha256b64(verifier) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return b64urlEncode(digest);
  }

  let _endpoints = null;

  // Discover Shopify's authorization endpoint from the storefront domain.
  async function getEndpoints() {
    if (_endpoints) return _endpoints;
    const res = await fetch(location.origin + '/.well-known/openid-configuration', { credentials: 'omit' });
    if (!res.ok) throw new Error('openid-configuration failed: ' + res.status);
    const j = await res.json();
    _endpoints = { authorizationEndpoint: j.authorization_endpoint, tokenEndpoint: j.token_endpoint };
    return _endpoints;
  }

  // The URL Shopify redirects back to after login — origin + path, no query.
  // Must be registered as a Callback URL in the Headless channel.
  function caRedirectUri() {
    return location.origin + location.pathname;
  }

  // Kick off the Shopify hosted login. Returns true when the redirect fired.
  async function startLogin() {
    if (!caClientId()) { console.warn('[KKL quiz] No Customer Account API clientId configured.'); return false; }
    try {
      if (!crypto || !crypto.subtle) { console.warn('[KKL quiz] Secure context required for PKCE.'); return false; }
      const ep = await getEndpoints();
      const verifier = randomString(64);
      const challenge = await sha256b64(verifier);
      const state = randomString(32);
      storeSet(CA_VERIFIER_KEY, verifier);
      storeSet(CA_STATE_KEY, state);
      const u = new URL(ep.authorizationEndpoint);
      u.searchParams.set('client_id', caClientId());
      u.searchParams.set('redirect_uri', caRedirectUri());
      u.searchParams.set('response_type', 'code');
      u.searchParams.set('scope', CA_SCOPE);
      u.searchParams.set('state', state);
      u.searchParams.set('nonce', randomString(32));
      u.searchParams.set('code_challenge', challenge);
      u.searchParams.set('code_challenge_method', 'S256');
      location.href = u.toString();
      return true;
    } catch (e) {
      console.warn('[KKL quiz] Failed to start CA login:', e);
      return false;
    }
  }

  // Keep the to-be-saved payload across the login round-trip.
  function queueSave(payload) {
    storeSet(CA_PENDING_KEY, JSON.stringify(payload));
  }
  function popPending() {
    const s = storeGet(CA_PENDING_KEY);
    storeDel(CA_PENDING_KEY);
    if (!s) return null;
    try { return JSON.parse(s); } catch (e) { return null; }
  }

  // Strip ?code=&state= from the URL so a refresh never re-processes the code.
  function cleanupAuthQuery() {
    try {
      const u = new URL(location.href);
      if (u.searchParams.get('code') || u.searchParams.get('state') || u.searchParams.get('error')) {
        u.searchParams.delete('code');
        u.searchParams.delete('state');
        u.searchParams.delete('error');
        history.replaceState(null, '', u.pathname + u.search + u.hash);
      }
    } catch (e) { /* noop */ }
  }

  // Handle the return from the hosted login:
  //  - validates state (CSRF),
  //  - if a pending payload exists → POST /api/quiz-save (exchange + write),
  //  - otherwise → POST /api/quiz-load (exchange + read saved assessment).
  // Never stores a token in the browser. Resolves { ok, saved?, profile?, error? }.
  async function completeCallback() {
    const out = { ok: false };
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code) return out; // not an auth callback

    const storedState = storeGet(CA_STATE_KEY);
    storeDel(CA_STATE_KEY);
    if (!state || !storedState || state !== storedState) {
      console.warn('[KKL quiz] OAuth state mismatch — aborting.');
      return out;
    }
    const verifier = storeGet(CA_VERIFIER_KEY) || '';
    storeDel(CA_VERIFIER_KEY);

    const apiUrl = caApiUrl();
    if (!apiUrl) { out.error = 'apiUrl not configured'; return out; }

    const pending = popPending();
    try {
      const path = pending ? '/api/quiz-save' : '/api/quiz-load';
      const body = {
        code,
        code_verifier: verifier,
        redirect_uri: caRedirectUri()
      };
      if (pending) body.payload = pending;

      const res = await fetch(apiUrl + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        out.error = (json && json.error) || ('HTTP ' + res.status);
        return out;
      }

      if (pending) {
        out.ok = !!(json && json.ok);
        out.saved = out.ok;
        if (!out.ok) out.error = (json && json.error) || 'save-failed';
      } else {
        out.ok = true;
        const cloudProfile = (json && json.profile) || null;
        out.cloudEmpty = !cloudProfile;
        const saveIntent = sessionStorage.getItem('kkl_quiz_save_intent') === '1';
        
        // Policy: Intent-First.
        // If the user explicitly finished a quiz or clicked save, use local data.
        // Otherwise (passive login), use Cloud data as absolute truth for security.
        if (saveIntent) {
          out.profile = loadProfile();
          out.needsSync = true;
          sessionStorage.removeItem('kkl_quiz_save_intent');
        } else if (cloudProfile) {
          out.profile = cloudProfile;
          out.needsSync = false;
        } else {
          out.profile = loadProfile();
          out.needsSync = !!out.profile;
        }
      }
      return out;
    } catch (e) {
      out.error = String(e);
      return out;
    }
  }

  // Same payload shape as the localStorage profile, so the two stay interchangeable.
  function buildPayload(profile, priorities, answers, result, isSynced = false) {
    // Format timestamp in America/New_York (Eastern Time)
    const nyTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(new Date());

    // Convert format "MM/DD/YYYY, HH:mm:ss" to ISO-like "YYYY-MM-DDTHH:mm:ss"
    const [date, time] = nyTime.split(', ');
    const [m, d, y] = date.split('/');
    const timestamp = `${y}-${m}-${d}T${time}`;

    return {
      profile, priorities, answers,
      scores: result.pct,
      primary: result.primary,
      secondary: result.secondary,
      updatedAt: timestamp,
      isSynced
    };
  }

  function clearProfile() {
    try { localStorage.removeItem(PROFILE_KEY); } catch (e) { /* noop */ }
  }

  function saveProfile(profile, priorities, answers, result, isSynced = false) {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(buildPayload(profile, priorities, answers, result, isSynced)));
    } catch (e) { /* noop */ }
  }
  function loadProfile() {
    try {
      const s = localStorage.getItem(PROFILE_KEY);
      return s ? JSON.parse(s) : null;
    } catch (e) { return null; }
  }

  function track(companyId, email, name, result) {
    if (!companyId) return;
    window._learnq = window._learnq || [];
    window._learnq.push(['identify', { $email: email, $first_name: name }]);
    window._learnq.push(['track', 'Quiz Completed', {
      primary: result.primary,
      secondary: result.secondary,
      scores: result.pct
    }]);
  }

  return {
    selectQuestions, compute, clearProfile,
    saveProfile, loadProfile, buildPayload,
    track,
    startLogin, queueSave, popPending, completeCallback, cleanupAuthQuery
  };
})();
